import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import PostCard from "@/components/feature/PostCard";
import { useDocumentMeta, SITE_ORIGIN } from "@/hooks/useDocumentMeta";
import { communityPosts } from "@/mocks/posts";
import { communityCategories } from "@/mocks/categories";
import HotFilterBar, { type SortKey } from "./components/HotFilterBar";

const validSorts: SortKey[] = ["heat", "latest", "replies"];

export default function HotList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") ?? "all";
  const rawSort = searchParams.get("sort") as SortKey | null;
  const sort: SortKey = rawSort && validSorts.includes(rawSort) ? rawSort : "heat";
  const qParam = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(qParam);

  useEffect(() => {
    setKeyword(qParam);
  }, [qParam]);

  const categoryName =
    category === "all"
      ? "全部热帖"
      : communityCategories.find((c) => c.slug === category)?.name ?? "全部热帖";

  function updateParams(patch: Record<string, string>) {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next, { replace: true });
  }

  const results = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = communityPosts.filter((post) => {
      if (category === "essence") {
        if (!post.essence) return false;
      } else if (category !== "all" && post.categorySlug !== category) {
        return false;
      }
      if (kw) {
        const haystack = `${post.title} ${post.summary} ${post.author} ${post.tags.join(" ")}`.toLowerCase();
        if (!haystack.includes(kw)) return false;
      }
      return true;
    });

    return list.sort((a, b) => {
      if (sort === "latest") return Number(b.id) - Number(a.id);
      if (sort === "replies") return b.replyCount - a.replyCount;
      return b.heat - a.heat;
    });
  }, [category, keyword, sort]);

  const pageTitle = `黑料网社热帖榜 - ${categoryName}｜高赞神回复与热评排行`;
  const pageDesc = `浏览黑料网社${categoryName}，共 ${results.length} 条内容。按热度、最新发布或热评数量排序，查看热帖正文与加精神回复，围观今日最火的讨论。`;
  const keywords = `黑料网社热帖榜,黑料网社高赞,${categoryName},热帖排行,神回复`;

  useDocumentMeta({
    title: pageTitle,
    description: pageDesc,
    keywords,
    path: "/hot",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `黑料网社热帖榜 · ${categoryName}`,
        description: pageDesc,
        url: `${SITE_ORIGIN}/hot`,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "热帖榜", item: `${SITE_ORIGIN}/hot` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `${categoryName}热帖`,
        itemListElement: results.slice(0, 10).map((post, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: post.title,
          url: `${SITE_ORIGIN}/post/${post.id}`,
        })),
      },
    ],
  });

  return (
    <SiteLayout>
      <section className="w-full bg-foreground-950 pt-24 md:pt-28 pb-10 md:pb-14 paper-grain">
        <div className="max-w-content mx-auto px-4 md:px-8">
          <div className="[&_a]:text-background-300 [&_a:hover]:text-primary-200 [&_span]:text-background-200 [&_.ri-arrow-right-s-line]:text-background-500">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "热帖榜" }]} />
          </div>
          <h1 className="mt-5 font-heading text-3xl md:text-5xl font-black text-background-50 leading-tight">
            黑料网社热帖榜
          </h1>
          <p className="mt-4 text-sm md:text-base text-background-300 max-w-2xl leading-relaxed">
            汇总站内正在被围观的热帖。你可以用关键词搜索，也可以按分类切换，
            排序方式随时调整——热度、最新、热评最多，各取所需。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="max-w-content mx-auto px-4 md:px-8 py-6 md:py-8">
          <HotFilterBar
            category={category}
            sort={sort}
            keyword={keyword}
            resultCount={results.length}
            onCategoryChange={(slug) => updateParams({ category: slug === "all" ? "" : slug })}
            onSortChange={(value) => updateParams({ sort: value })}
            onKeywordChange={(value) => {
              setKeyword(value);
              updateParams({ q: value });
            }}
          />
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="max-w-content mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              {categoryName}
              {keyword.trim() && (
                <span className="ml-2 text-sm font-normal text-foreground-500">
                  关键词「{keyword.trim()}」
                </span>
              )}
            </h2>
            <span className="text-sm text-foreground-500">按「{sort === "heat" ? "热度优先" : sort === "latest" ? "最新发布" : "热评最多"}」排列</span>
          </div>

          {results.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {results.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center border border-dashed border-background-300 rounded-lg">
              <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">
                这个分类暂时没搜到内容
              </h3>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词，或者点上面的分类看看别处正在围观什么。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams(new URLSearchParams(), { replace: true });
                }}
                className="mt-6 px-5 py-2.5 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选
              </button>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}